import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { EDITOR_CLASS_SELECTOR } from '@remirror/core-constants';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _classCallCheck from '@babel/runtime/helpers/classCallCheck';
import _createClass from '@babel/runtime/helpers/createClass';
import _toArray from '@babel/runtime/helpers/toArray';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { isObject, clamp, isNumber, bool, isPlainObject, isArray, isString, isUndefined, get, deepMerge, isFunction } from '@remirror/core-helpers';
import { css, ThemeContext, jsx, Global } from '@emotion/core';
import { createContext, useContext, useState, useMemo } from 'react';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import { css as css$1 } from '@styled-system/css';
import { ThemeProvider } from 'emotion-theming';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
// Taken from https://github.com/regexhq/hsla-regex/blob/master/test/test.js#L7
var HSLA_REGEX = /^hsla\((\d+),\s*([\d.]+)%,\s*([\d.]+)%,\s*(\d*(?:\.\d+)?)\)$/; // Taken from https://github.com/regexhq/hsl-regex/blob/3ea5cede63c1e4a30878e7329a197c502337b7b3/index.js#L7

var HSL_REGEX = /^hsl\(\s*([\d.]+)\s*,\s*(\d*(?:\.\d+)?%)\s*,\s*(\d*(?:\.\d+)?%)\)$/;
var HSL_SYMBOL = Symbol.for('hslColor');
var isHSL = function isHSL(value) {
  return isObject(value) && value.constructor && value.constructor.$$id === HSL_SYMBOL;
};

var isNamedHSLObject = function isNamedHSLObject(value) {
  return bool(isPlainObject(value) && value.hue && value.saturation && value.lightness);
};

var isHSLObject = function isHSLObject(value) {
  return bool(isPlainObject(value) && value.h && value.s && value.l);
};

var hslArrayToObject = function hslArrayToObject(value) {
  var _value = _slicedToArray(value, 4),
      h = _value[0],
      s = _value[1],
      l = _value[2],
      a = _value[3];

  return {
    h: h,
    s: s,
    l: l,
    a: a
  };
};

var hslStringToObject = function hslStringToObject(value) {
  var regexValue = HSLA_REGEX.exec(value) || HSL_REGEX.exec(value);

  if (!regexValue) {
    throw new Error("Invalid HSL input string used: '".concat(value, "'"));
  }

  var _regexValue = _toArray(regexValue),
      array = _regexValue.slice(1);

  return hslArrayToObject(array);
};

var convertHSLToObject = function convertHSLToObject(value) {
  if (isHSLObject(value)) {
    return value;
  }

  if (isNamedHSLObject(value)) {
    var h = value.hue,
        s = value.saturation,
        l = value.lightness,
        a = value.alpha;
    return {
      h: h,
      s: s,
      l: l,
      a: a
    };
  }

  if (isArray(value)) {
    return hslArrayToObject(value);
  }

  if (isString(value)) {
    return hslStringToObject(value);
  }

  throw new Error("Invalid HSL input value passed: ".concat(isPlainObject(value) ? JSON.stringify(value) : value));
};

var isValidHue = function isValidHue(hue) {
  return isNumber(hue) && hue <= 360 && hue >= 0;
};

var createValidHue = function createValidHue(value) {
  var hue = isString(value) ? Number(value) % 360 : value % 360;

  if (!isValidHue(hue)) {
    throw new Error("Invalid 'hue': ".concat(value));
  }

  return hue;
};

var isValidPercent = function isValidPercent(percent) {
  return isNumber(percent) && percent <= 100 && percent >= 0;
};

var createValidPercent = function createValidPercent(value) {
  var percent = isString(value) ? Number(value.replace(/[%\s]+/g, '')) : value;

  if (!isValidPercent(percent)) {
    throw new Error("Invalid 'percentage': ".concat(value));
  }

  return percent;
};

var clampPercent = function clampPercent(value) {
  return clamp({
    min: 0,
    max: 100,
    value: value
  });
};

var createValidAlpha = function createValidAlpha(value) {
  var alpha = isString(value) ? value.includes('%') ? Number(value.replace(/[%\s]+/g, '')) : Number(value) * 100 : value;

  if (isUndefined(alpha)) {
    return createValidPercent(100);
  }

  return createValidPercent(alpha);
};
/**
 * Create a valid hsl object with numbers as value. Throws an error if
 */


var createValidHSLObject = function createValidHSLObject(_ref) {
  var hue = _ref.h,
      saturation = _ref.s,
      lightness = _ref.l,
      alpha = _ref.a;
  return {
    h: createValidHue(hue),
    s: createValidPercent(saturation),
    l: createValidPercent(lightness),
    a: createValidAlpha(alpha)
  };
};

var freeze = function freeze(hslObject) {
  return Object.freeze(hslObject);
};
/**
 * Taken from https://github.com/usemeta/hsl-rgb/blob/1afed9a9703816e375839b4fbb6458f2765a76a8/index.js#L1
 */


var calculateHue = function calculateHue(pp, qq, hh) {
  hh = hh < 0 ? hh + 1 : hh > 1 ? hh - 1 : hh;
  return hh < 1 / 6 ? pp + (qq - pp) * 6 * hh : hh < 1 / 2 ? qq : hh < 2 / 3 ? pp + (qq - pp) * (2 / 3 - hh) * 6 : pp;
};
/**
 * Taken from https://github.com/usemeta/hsl-rgb/blob/1afed9a9703816e375839b4fbb6458f2765a76a8/index.js#L1
 */


var hslToRgb = function hslToRgb(_ref2) {
  var _ref3 = _slicedToArray(_ref2, 3),
      hue = _ref3[0],
      saturation = _ref3[1],
      lightness = _ref3[2];

  var rr = 0,
      gg = 0,
      bb = 0;
  var hh = hue / 360;
  var ss = saturation / 100;
  var ll = lightness / 100;

  if (ss === 0) {
    rr = gg = bb = ll;
  } else {
    var qq = ll < 0.5 ? ll * (1 + ss) : ll + ss - ll * ss;
    var pp = 2 * ll - qq;
    rr = calculateHue(pp, qq, hh + 1 / 3);
    gg = calculateHue(pp, qq, hh);
    bb = calculateHue(pp, qq, hh - 1 / 3);
  }

  return [Math.max(0, Math.min(Math.round(rr * 255), 255)), Math.max(0, Math.min(Math.round(gg * 255), 255)), Math.max(0, Math.min(Math.round(bb * 255), 255))];
};
/**
 * Convert a percentage to a ratio while avoid
 */


var percentageToRatio = function percentageToRatio(percent) {
  var precision = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 10;
  return Number((percent / 100).toFixed(precision));
};
/**
 * HSLColor is a small utility for transforming colors, but only hsl colors.
 */


var HSL =
/*#__PURE__*/
function () {
  _createClass(HSL, null, [{
    key: "create",

    /**
     * Create a hsl class from the passed in string / array / object parameters.
     *
     * Throws a descriptive error if invalid parameters are passed in.
     */
    value: function create(value) {
      if (isHSL(value)) {
        return value.clone();
      }

      var hsl = createValidHSLObject(convertHSLToObject(value));
      return new HSL(hsl);
    }
    /**
     * Store the hue as an object of number values. Percentages are stored as decimals
     * between 0 and 1.
     */

  }]);

  function HSL(hsla) {
    _classCallCheck(this, HSL);

    this.hsla = freeze(hsla);
  }
  /**
   * Hue
   *
   * A value between 0 and 360 representing the color wheel rotation.
   */


  _createClass(HSL, [{
    key: "toJSON",

    /**
     * Returns the object representation of the hsl class.
     */
    value: function toJSON() {
      var h = this.h,
          s = this.s,
          l = this.l,
          a = this.a;
      return {
        h: h,
        s: s,
        l: l,
        a: a
      };
    }
    /**
     * Return the array representation of the hsl
     */

  }, {
    key: "toArray",
    value: function toArray() {
      var h = this.h,
          s = this.s,
          l = this.l,
          a = this.a;
      return [h, s, l, a];
    }
    /**
     * Produces a valid HSL string that can be used to represent a color in your css.
     */

  }, {
    key: "toString",
    value: function toString() {
      var h = this.h,
          s = this.s,
          l = this.l,
          a = this.a;
      var _ref4 = [h.toString(), "".concat(s, "%"), "".concat(l, "%"), "".concat(percentageToRatio(a))],
          hue = _ref4[0],
          saturation = _ref4[1],
          lightness = _ref4[2],
          alpha = _ref4[3];
      return alpha === '1' ? "hsl(".concat(hue, ", ").concat(saturation, ", ").concat(lightness, ")") : "hsla(".concat(hue, ", ").concat(saturation, ", ").concat(lightness, ", ").concat(alpha, ")");
    }
    /**
     * Returns the array
     */

  }, {
    key: "toRGB",
    value: function toRGB() {
      var h = this.h,
          s = this.s,
          l = this.l,
          a = this.a;

      var _hslToRgb = hslToRgb([h, s, l]),
          _hslToRgb2 = _slicedToArray(_hslToRgb, 3),
          r = _hslToRgb2[0],
          g = _hslToRgb2[1],
          b = _hslToRgb2[2];

      return [r, g, b, a];
    }
    /**
     * Rotate the color around the color wheel. The value is always clamped between 0 and 360deg.
     *
     * ```ts
     * const hsl = HSL.create('hsl(180, 50%, 50%)')
     * color.rotate(100).h // => 280
     * ```
     */

  }, {
    key: "rotate",
    value: function rotate(degrees) {
      var _this$hsla = this.hsla,
          h = _this$hsla.h,
          sla = _objectWithoutProperties(_this$hsla, ["h"]);

      return this.clone(_objectSpread({}, sla, {
        h: createValidHue(h + degrees)
      }));
    }
    /**
     * Lighten the color.
     *
     * Can either be a number or a string which represents a percentage.
     *
     * ```ts
     * const hsl = HSL.create('hsl(180, 50%, 50%)')
     * color.lighten(50).l // => 75
     * color.lighten('25%').l // => 62.5
     * ```
     */

  }, {
    key: "lighten",
    value: function lighten(percent) {
      var _this$hsla2 = this.hsla,
          l = _this$hsla2.l,
          hsa = _objectWithoutProperties(_this$hsla2, ["l"]);

      var lightness = createValidPercent(clampPercent(l + createValidPercent(percent)));
      return this.clone(_objectSpread({}, hsa, {
        l: lightness
      }));
    }
    /**
     * Darken the color.
     *
     * Can either be a number or a string which represents a percentage.
     *
     * ```ts
     * const hsl = HSL.create('hsl(180, 50%, 50%)')
     * color.darken(50).l // => 25
     * color.darken('25%').l // => 37.5
     * ```
     */

  }, {
    key: "darken",
    value: function darken(percent) {
      var _this$hsla3 = this.hsla,
          l = _this$hsla3.l,
          hsa = _objectWithoutProperties(_this$hsla3, ["l"]);

      var lightness = createValidPercent(clampPercent(l - createValidPercent(percent)));
      return this.clone(_objectSpread({}, hsa, {
        l: lightness
      }));
    }
    /**
     * Increase the opacity of the color.
     *
     * Can either be a number or a string which represents a percentage.
     *
     * ```ts
     * const hsl = HSL.create('hsla(180, 50%, 50%, 0.5)')
     * color.unfade(40).a // => 90
     * color.unfade('25%').a // => 75
     * ```
     */

  }, {
    key: "unfade",
    value: function unfade(percent) {
      var _this$hsla4 = this.hsla,
          _this$hsla4$a = _this$hsla4.a,
          a = _this$hsla4$a === void 0 ? 100 : _this$hsla4$a,
          hsl = _objectWithoutProperties(_this$hsla4, ["a"]);

      var alpha = createValidPercent(clampPercent(a + createValidPercent(percent)));
      return this.clone(_objectSpread({}, hsl, {
        a: alpha
      }));
    }
    /**
     * Decrease the opacity of the color.
     *
     * Can either be a number or a string which represents a percentage.
     *
     * ```ts
     * const hsl = HSL.create('hsla(180, 50%, 50%, 0.5)')
     * color.fade(40).a // => 10
     * color.fade('25%').a // => 25
     * ```
     */

  }, {
    key: "fade",
    value: function fade(percent) {
      var _this$hsla5 = this.hsla,
          _this$hsla5$a = _this$hsla5.a,
          a = _this$hsla5$a === void 0 ? 100 : _this$hsla5$a,
          hsl = _objectWithoutProperties(_this$hsla5, ["a"]);

      var num = clampPercent(a - createValidPercent(percent));
      var alpha = createValidPercent(num);
      return this.clone(_objectSpread({}, hsl, {
        a: alpha
      }));
    }
    /**
     * Increase the color saturation.
     *
     * Can either be a number or a string which represents a percentage.
     *
     * NOTE: the number is treated as a percentage value (not a ratio).
     * - `50` is treated as `50%`
     * - `0.5` is treated as `0.5%`
     *
     * ```ts
     * const hsl = HSL.create('hsl(180, 50%, 50%)')
     * hsl.saturate(50).s // => 75
     * hsl.saturate('100%').s // => 100
     * ```
     */

  }, {
    key: "saturate",
    value: function saturate(percent) {
      var _this$hsla6 = this.hsla,
          s = _this$hsla6.s,
          hla = _objectWithoutProperties(_this$hsla6, ["s"]);

      var saturation = createValidPercent(clampPercent(s + createValidPercent(percent)));
      return this.clone(_objectSpread({}, hla, {
        s: saturation
      }));
    }
    /**
     * Decrease the color saturation.
     *
     * Can either be a number or a string which represents a percentage.
     *
     * ```ts
     * const hsl = HSL.create('hsl(180, 50%, 50%)')
     * hsl.desaturate(50).s // => 25
     * hsl.desaturate('100%').s // => 0
     * ```
     */

  }, {
    key: "desaturate",
    value: function desaturate(percent) {
      var _this$hsla7 = this.hsla,
          s = _this$hsla7.s,
          hla = _objectWithoutProperties(_this$hsla7, ["s"]);

      var saturation = createValidPercent(clampPercent(s - createValidPercent(percent)));
      return this.clone(_objectSpread({}, hla, {
        s: saturation
      }));
    }
    /**
     * Set the hue value of the color.
     *
     * ```ts
     * const hsl = HSL.create('hsl(180, 50%, 50%)');
     * hsl.hue(100).h //=> 100
     * ```
     */

  }, {
    key: "hue",
    value: function hue(_hue) {
      return this.clone(_objectSpread({}, this.hsla, {
        h: createValidHue(_hue)
      }));
    }
    /**
     * Set the saturation value of the color.
     *
     * ```ts
     * const hsl = HSL.create('hsl(180, 50%, 50%)');
     * hsl.saturation(15).s //=> 15
     * ```
     */

  }, {
    key: "saturation",
    value: function saturation(_saturation) {
      return this.clone(_objectSpread({}, this.hsla, {
        s: createValidPercent(_saturation)
      }));
    }
    /**
     * Set the lightness value of the color.
     *
     * ```ts
     * const hsl = HSL.create('hsl(180, 50%, 50%)');
     * hsl.lightness(100).h //=> 100
     * ```
     */

  }, {
    key: "lightness",
    value: function lightness(_lightness) {
      return this.clone(_objectSpread({}, this.hsla, {
        l: createValidPercent(_lightness)
      }));
    }
    /**
     * Set the alpha (transparency) value of the color.
     *
     * Value passed must be a percentage;
     *
     * ```ts
     * const hsl = HSL.create('hsl(180, 50%, 50%, 0.5)');
     * hsl.alpha(40).a // => 40
     * ```
     */

  }, {
    key: "alpha",
    value: function alpha(_alpha) {
      return this.clone(_objectSpread({}, this.hsla, {
        a: createValidAlpha(isNumber(_alpha) ? "".concat(_alpha, "%") : _alpha)
      }));
    }
    /**
     * Returns a cloned version of the HSL.
     *
     * If an HSLObject is provided it merges these properties into the final object.
     */

  }, {
    key: "clone",
    value: function clone() {
      var hsla = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : Object.create(null);
      return new HSL(createValidHSLObject(_objectSpread({}, this.hsla, {}, hsla)));
    }
    /* Color Harmonies
    Docs taken from https://www.tigercolor.com/color-lab/color-theory/color-harmonies.htm */

    /**
     * Colors that are opposite each other on the color wheel are considered to be complementary colors (example: red and green).
     *
     * The high contrast of complementary colors creates a vibrant look especially when used at full saturation. This color scheme must be managed well so it is not jarring.
     * Complementary colors are tricky to use in large doses, but work well when you want something to stand out.
     *
     * In general, complementary colors are really bad for text.
     */

  }, {
    key: "complement",
    value: function complement() {
      return this.rotate(180);
    }
    /**
     * Analogous color schemes use colors that are next to each other on the color wheel.
     * They usually match well and create serene and comfortable designs.
     *
     * Analogous color schemes are often found in nature and are harmonious and pleasing to the eye.
     * Make sure you have enough contrast when choosing an analogous color scheme.
     *
     * Choose one color to dominate, a second to support. The third color is used (along with black,
     * white or gray) as an accent.
     */

  }, {
    key: "analagous",
    value: function analagous() {
      var direction = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'forwards';

      var _ref5 = direction === 'forwards' ? [30, 60] : [-30, -60],
          _ref6 = _slicedToArray(_ref5, 2),
          secondary = _ref6[0],
          tertiary = _ref6[1];

      return [this, this.rotate(secondary), this.rotate(tertiary)];
    }
    /**
     * A triadic color scheme uses colors that are evenly spaced around the color wheel.
     *
     * Triadic color harmonies tend to be quite vibrant, even if you use pale or unsaturated
     * versions of your hues.
     *
     * To use a triadic harmony successfully, the colors should be carefully balanced - let one
     * color dominate and use the two others for accent.
     */

  }, {
    key: "triadic",
    value: function triadic() {
      return [this, this.rotate(120), this.rotate(240)];
    }
    /**
     * The split complementary color scheme is a variation of the complementary color scheme.
     * In addition to the base color, it uses the two colors adjacent to its complement.
     *
     * This color scheme has the same strong visual contrast as the complementary color scheme,
     * but has less tension.
     *
     * The split-complimentary color scheme is often a good choice for beginners, because it is
     * difficult to mess up.
     */

  }, {
    key: "splitComplement",
    value: function splitComplement() {
      return [this, this.rotate(150), this.rotate(210)];
    }
    /**
     * The rectangle or tetradic color scheme uses four colors arranged into two
     * complementary pairs.
     *
     * This rich color scheme offers plenty of possibilities for variation.
     * The tetradic color scheme works best if you let one color be dominant.
     *
     * You should also pay attention to the balance between warm and cool colors
     * in your design.
     *
     * @param spacing - a value between 0 and 180 that separates the edges in degrees around a color wheel.
     */

  }, {
    key: "rectangle",
    value: function rectangle() {
      var spacing = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 60;
      spacing = clamp({
        min: 0,
        max: 180,
        value: spacing
      });
      return [this, this.rotate(spacing), this.rotate(180), this.rotate(180 + spacing)];
    }
    /**
     * The square color scheme is similar to the rectangle, but with all four colors spaced
     * evenly around the color circle.
     *
     * The square color scheme works best if you let one color be dominant.
     *
     * You should also pay attention to the balance between warm and cool colors in your design.
     */

  }, {
    key: "square",
    value: function square() {
      return this.rectangle(90);
    }
  }, {
    key: "h",
    get: function get() {
      return this.hsla.h;
    }
    /**
     * Saturation
     *
     * A value between 0 and 100 representing the percentage saturation.
     */

  }, {
    key: "s",
    get: function get() {
      return this.hsla.s;
    }
    /**
     * Lightness
     *
     * A value between 0 and 100 representing the percentage lightness.
     */

  }, {
    key: "l",
    get: function get() {
      return this.hsla.l;
    }
    /**
     * Alpha
     *
     * The alpha value which is a number between 0 and 1.
     */

  }, {
    key: "a",
    get: function get() {
      return this.hsla.a;
    }
    /**
     * Brightness
     *
     * Returns a number between 0 and 255 the current color (not taking into account the alpha level).
     * The higher the number the brighter the color.
     *
     * https://24ways.org/2010/calculating-color-contrast
     */

  }, {
    key: "brightness",
    get: function get() {
      var _this$toRGB = this.toRGB(),
          _this$toRGB2 = _slicedToArray(_this$toRGB, 3),
          r = _this$toRGB2[0],
          g = _this$toRGB2[1],
          b = _this$toRGB2[2];

      return (r * 299 + g * 587 + b * 114) / 1000;
    }
  }, {
    key: "isDark",
    get: function get() {
      return this.brightness < 128;
    }
  }, {
    key: "isLight",
    get: function get() {
      return !this.isDark;
    }
  }]);

  return HSL;
}();

_defineProperty(HSL, "$$id", HSL_SYMBOL);

var _remirrorEditor;

function ownKeys$1(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$1(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$1(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$1(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
var baseHue = 205;
var baseSaturation = 70;
var baseLightness = 50;
var primary = HSL.create([baseHue, baseSaturation, baseLightness]);
var hsl = {
  primary: primary,
  secondary: primary.complement(),
  background: HSL.create([0, 0, 100]),
  text: HSL.create([baseHue, 5, 5]),
  grey: HSL.create([baseHue, 5, 80]),
  darkGrey: HSL.create([baseHue, 5, 50]),
  muted: HSL.create([baseHue, 97.5, 97.5]),
  light: HSL.create([baseHue, 5, 90])
};

var str = function str(color) {
  for (var _len = arguments.length, methods = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    methods[_key - 1] = arguments[_key];
  }

  return methods.reduce(function (col, method) {
    return method(col);
  }, color).toString();
};

var active = function active(color) {
  return color.darken(10);
};

var hover = function hover(color) {
  return color.darken(5).saturate(9);
}; // const fade = (amount: number) => (color: HSL) => color.fade(amount);


var alpha = function alpha(amount) {
  return function (color) {
    return color.alpha(amount);
  };
};

var colors = {
  text: str(hsl.text),
  background: str(hsl.background),
  'background:button:hover': str(hsl.background, hover),
  'background:button:active': str(hsl.background, active),
  primary: str(hsl.primary),
  'primary:button:hover': str(primary, hover),
  'primary:button:active': str(primary, active),
  secondary: str(hsl.secondary),
  'secondary:button:hover': str(hsl.secondary, hover),
  'secondary:button:active': str(hsl.secondary, active),
  muted: str(hsl.muted),
  grey: str(hsl.grey),
  darkGrey: str(hsl.darkGrey),
  'grey:fade': str(hsl.grey, alpha(20)),
  default: str(hsl.grey),
  'default:button:hover': str(hsl.grey, hover),
  'default:button:active': str(hsl.grey, active),
  light: str(hsl.light),
  modes: {
    dark: {
      text: str(hsl.background),
      background: str(hsl.text.alpha(0.9))
    }
  }
};
var zIndices = {
  auto: 'auto',
  '0': 0,
  '10': 10,
  '20': 20,
  '30': 30,
  '40': 40,
  '50': 50
};
var letterSpacings = {
  tighter: '-0.05em',
  tight: '-0.025em',
  normal: '0',
  wide: '0.025em',
  wider: '0.05em',
  widest: '0.1em'
};
var baseLineHeights = {
  none: '1',
  tight: '1.25',
  snug: '1.375',
  normal: '1.5',
  relaxed: '1.625',
  loose: '2'
};
var heading = {
  fontFamily: 'heading',
  fontWeight: 'heading',
  lineHeight: 'heading',
  letterSpacings: 'wider',
  m: 0,
  mb: 1
};
/**
 * This is the default theme used throughout the built in ui-components.
 */

var baseTheme = {
  initialColorMode: 'light',
  colors: colors,
  space: [0, 4, 8, 16, 32, 64, 128, 256, 512],
  sizes: [0, 4, 8, 16, 32, 64, 128, 256, 512],
  fonts: {
    body: 'BlinkMacSystemFont,-apple-system,"Segoe UI",Roboto,Oxygen,Ubuntu,Cantarell,"Fira Sans","Droid Sans","Helvetica Neue",Helvetica,Arial,sans-serif',
    heading: 'inherit',
    monospace: 'Menlo, monospace'
  },
  zIndices: zIndices,
  letterSpacings: letterSpacings,
  radii: [0, 4, 8, 16, 32, 64, 128],
  fontSizes: [12, 14, 16, 20, 24, 32, 48, 64, 96],
  fontWeights: {
    body: 400,
    heading: 600,
    bold: 700
  },
  lineHeights: _objectSpread$1({}, baseLineHeights, {
    body: baseLineHeights.relaxed,
    heading: baseLineHeights.tight
  }),
  shadows: {
    focus: 'hsla(205, 73%, 57%, 0.6) 0px 0px 0px 3px',
    buttons: '0 0 0 0 hsla(205, 6%, 14%, 0.15) inset',
    text: 'none',
    card: "".concat(HSL.create([baseHue, baseSaturation, 15, 25]), " 0 4px 8px -2px, ").concat(HSL.create([baseHue, baseSaturation, 15, 31]), " 0px 0px 1px")
  },
  'remirror:buttons': {
    default: {
      backgroundColor: 'light',
      minHeight: 3,
      color: 'text',
      fontFamily: 'body',
      verticalAlign: 'baseline',
      paddingY: 2,
      paddingX: 3,
      fontWeight: 'bold',
      borderRadius: 1,
      '&:hover': {
        backgroundColor: 'default:button:hover'
      },
      '&:active': {
        backgroundColor: 'default:button:active'
      }
    },
    primary: {
      variant: 'remirror:buttons.default',
      backgroundColor: 'primary',
      color: 'background',
      ':hover': {
        backgroundColor: 'primary:button:hover'
      },
      ':active': {
        backgroundColor: 'primary:button:active'
      }
    },
    secondary: {
      variant: 'remirror:buttons.default',
      backgroundColor: 'secondary',
      color: 'background',
      ':hover': {
        backgroundColor: 'secondary:button:hover'
      },
      ':active': {
        backgroundColor: 'secondary:button:active',
        ':hover': {
          backgroundColor: 'secondary:button:active'
        }
      }
    },
    background: {
      variant: 'remirror:buttons.default',
      backgroundColor: 'background',
      ':hover': {
        backgroundColor: 'background:button:hover'
      },
      ':active': {
        backgroundColor: 'background:button:active',
        ':hover': {
          backgroundColor: 'background:button:active'
        }
      }
    }
  },
  'remirror:icons': {
    default: {
      color: 'text',
      transition: 'all 0.2s',
      backgroundColor: 'background'
    },
    inverse: {
      variant: 'remirror:icons.default',
      color: 'background',
      backgroundColor: 'text'
    }
  },
  'remirror:text': {
    body: {
      fontSize: 'body',
      lineHeight: 'body',
      fontFamily: 'body',
      fontWeight: 'body',
      letterSpacings: 'wide'
    },
    caption: {},
    h1: _objectSpread$1({}, heading, {
      fontSize: 6,
      mt: 2
    }),
    h2: _objectSpread$1({}, heading, {
      fontSize: 5,
      mt: 2
    }),
    h3: _objectSpread$1({}, heading, {
      fontSize: 4,
      mt: 3
    }),
    h4: _objectSpread$1({}, heading, {
      fontSize: 3
    }),
    h5: _objectSpread$1({}, heading, {
      fontSize: 2
    }),
    h6: _objectSpread$1({}, heading, {
      fontSize: 1,
      mb: 2
    }),
    label: {}
  },
  styles: {
    'remirror:editor': (_remirrorEditor = {}, _defineProperty(_remirrorEditor, EDITOR_CLASS_SELECTOR, {
      caretColor: 'currentColor',
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
      fontSize: 2
    }), _defineProperty(_remirrorEditor, "".concat(EDITOR_CLASS_SELECTOR, ":focus"), {
      outline: 'none' // boxShadow: 'focus',

    }), _defineProperty(_remirrorEditor, "".concat(EDITOR_CLASS_SELECTOR, "[contenteditable=\"false\"]"), {
      whiteSpace: 'normal'
    }), _defineProperty(_remirrorEditor, "".concat(EDITOR_CLASS_SELECTOR, "[contenteditable=\"true\"]"), {
      whiteSpace: 'pre-wrap'
    }), _remirrorEditor)
  }
};

/**
 * Applies the selected color mode to the theme.
 *
 * Taken from https://github.com/system-ui/theme-ui/blob/7a92518ee0e71aaf42a3115b102254b50d16683d/packages/theme-ui/src/provider.js#L14-L20
 *
 * @param theme - the theme
 * @param mode - the color mode to apply to the theme
 */

var applyColorMode = function applyColorMode(theme, mode) {
  if (!mode) {
    return theme;
  }

  var modes = get('colors.modes', theme, {});
  return deepMerge(theme, {
    colors: get(mode, modes, {})
  });
};
/**
 * Get the color modes available on this theme.
 */

var getColorModes = function getColorModes(theme) {
  return Object.keys(get('colors.modes', theme, {}));
};
/**
 * Creates a path getter for the provided theme.
 */

var getFactory = function getFactory(theme) {
  return function (path, fallback) {
    return get(path, theme, fallback);
  };
};
/**
 * Checks whether an object passed is a serialized style
 */

var isSerializedStyle = function isSerializedStyle(val) {
  return isPlainObject(val) && isString(val.name) && isString(val.styles);
};
/**
 * Return true when a theme prop exists on the value passed in.
 */

var hasThemeProp = function hasThemeProp(val) {
  return isPlainObject(val) && isPlainObject(val.theme);
};

var getTheme = function getTheme(props) {
  return hasThemeProp(props) ? props.theme : props;
};
/**
 * A wrapper around the `@styled-system/css` for composing multiple themed
 * styles together in the `css` prop.
 *
 * ```tsx
 * const Component = () => {
 *   const { sx, css } = useRemirrorTheme();
 *   const stringStyles = css`
 *     font-weight: bold;
 *   `;
 *
 *   return <div css={sx({ variant: 'simple' }, stringStyles)} />;
 * };
 * ```
 */


var sx = function sx() {
  for (var _len = arguments.length, styles = new Array(_len), _key = 0; _key < _len; _key++) {
    styles[_key] = arguments[_key];
  }

  return function (props) {
    var theme = getTheme(props);
    return css(styles.map(function (style) {
      return isArray(style) ? sx.apply(void 0, _toConsumableArray(style))(theme) : isFunction(style) ? css$1(style(theme))(theme) : isSerializedStyle(style) ? style : isPlainObject(style) ? css$1(style)(theme) : style;
    }));
  };
};
var cssValueUnits = function cssValueUnits(val) {
  return isString(val) ? val.replace(/[0-9 ]+/g, '') : undefined;
};
/**
 * Transforms a number to
 */

var numberToPixels = function numberToPixels(val) {
  return cssValueUnits(val) ? "".concat(val).replace(/ /g, '') : "".concat(val, "px");
};

/**
 * A noop function that mimics the css emotion call but renders no output.
 *
 * @remarks
 * This is useful for enabling the library user to switch off emotion when the requirement
 * is to only use CSS styles. The specificity of emotion class names makes this difficult.
 */

var cssNoOp = function cssNoOp() {
  return undefined;
};
/**
 * Provides access to the theme context
 */


var EmotionThemeContext = ThemeContext;
var defaultRemirrorThemeValue = {
  theme: baseTheme,
  css: css,
  sx: sx,
  sxx: cssNoOp,
  get: getFactory(baseTheme),
  colorMode: baseTheme.initialColorMode,
  colorModes: getColorModes(baseTheme),
  setColorMode: function setColorMode() {}
};
var withoutEmotionProps = {
  css: cssNoOp,
  sx: cssNoOp
};
/**
 * Provides access to the styling methods from within the context tree.
 */

var RemirrorThemeContext = createContext(defaultRemirrorThemeValue);

/**
 * A hook for pulling the remirror theme from the react context.
 */

var useRemirrorTheme = function useRemirrorTheme() {
  return useContext(RemirrorThemeContext);
};
/**
 * A hook which returns the current remirror theme from the emotion context.
 *
 * If no theme exists it uses the default `baseTheme`.
 */

var useEmotionTheme = function useEmotionTheme() {
  return useContext(EmotionThemeContext);
};

function ownKeys$2(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread$2(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys$2(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys$2(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

/**
 * Check whether the theme has a parent.
 */
var hasParent = function hasParent(theme) {
  return bool(theme.parent);
};
/**
 * When true this is the outermost remirror theme in the component tree.
 *
 * Since RemirrorThemeProvider's support nesting it is necessary to check so
 * that inner themes can be safely merged into the outer theme values.
 */


var isRoot = function isRoot(theme) {
  return !hasParent(theme);
};
/**
 * A component that either extends the existing emotion theme with the provided theme
 * or it creates a new theme context for all children components nested in the tree.
 *
 * @remarks
 *
 * Supports nested themes.
 */


var RemirrorThemeProvider = function RemirrorThemeProvider(_ref) {
  var themeProp = _ref.theme,
      children = _ref.children,
      _ref$withoutEmotion = _ref.withoutEmotion,
      withoutEmotion = _ref$withoutEmotion === void 0 ? false : _ref$withoutEmotion,
      _ref$disableMerge = _ref.disableMerge,
      disableMerge = _ref$disableMerge === void 0 ? [] : _ref$disableMerge,
      initialColorMode = _ref.initialColorMode;
  var disableMergeWithParent = disableMerge.includes('parent');
  var disableMergeWithBase = disableMerge.includes('base');
  var disableMergeWithEmotion = disableMerge.includes('emotion');
  var outer = useRemirrorTheme();
  var emotionTheme = useEmotionTheme();

  var _useState = useState(initialColorMode || outer.colorMode),
      _useState2 = _slicedToArray(_useState, 2),
      colorMode = _useState2[0],
      setColorMode = _useState2[1];

  var parentEmotionTheme = isRoot(outer) && !disableMergeWithEmotion ? emotionTheme : {};
  var parent = disableMergeWithParent ? hasParent(outer) ? outer.parent : Object.create(null) : isRoot(outer) && disableMergeWithBase ? Object.create(null) : outer.theme;
  var themeWithoutColorMode = deepMerge(parentEmotionTheme, parent, isFunction(themeProp) ? themeProp(parent) : themeProp);
  var theme = applyColorMode(themeWithoutColorMode, colorMode);

  var _useMemo = useMemo(function () {
    return [getFactory(theme), getColorModes(theme)];
  }, [theme]),
      _useMemo2 = _slicedToArray(_useMemo, 2),
      get = _useMemo2[0],
      colorModes = _useMemo2[1];

  var value = _objectSpread$2({}, defaultRemirrorThemeValue, {}, withoutEmotion ? withoutEmotionProps : {
    sxx: function sxx() {
      return defaultRemirrorThemeValue.sx.apply(defaultRemirrorThemeValue, arguments)(theme);
    }
  }, {
    parent: parent,
    theme: theme,
    get: get,
    colorMode: colorMode,
    colorModes: colorModes,
    setColorMode: setColorMode
  });

  return jsx(ThemeProvider, {
    theme: theme
  }, jsx(RemirrorThemeContext.Provider, {
    value: value
  }, children));
};

/** @jsx jsx */

/**
 * Provides the root component which can be placed within the remirror theme content
 * to apply the default body font to all children.
 */
var RemirrorThemeRoot = function RemirrorThemeRoot(_ref) {
  var children = _ref.children,
      _ref$makeGlobal = _ref.makeGlobal,
      makeGlobal = _ref$makeGlobal === void 0 ? false : _ref$makeGlobal;

  var _useRemirrorTheme = useRemirrorTheme(),
      sxx = _useRemirrorTheme.sxx;

  var styles = sxx({
    fontFamily: 'body'
  });
  return jsx("div", {
    css: styles,
    id: "remirror-theme-root"
  }, makeGlobal && jsx(Global, {
    styles: styles
  }), children);
};

export { EmotionThemeContext, HSL, RemirrorThemeContext, RemirrorThemeProvider, RemirrorThemeRoot, applyColorMode, baseTheme, cssValueUnits, defaultRemirrorThemeValue, getColorModes, getFactory, hasThemeProp, isHSL, isSerializedStyle, numberToPixels, sx, useEmotionTheme, useRemirrorTheme, withoutEmotionProps };
//# sourceMappingURL=ui.esm.js.map
